import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FiArrowLeft } from "react-icons/fi";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  assignProcedure,
  closeProcedure,
  rejectProcedure,
} from "../../../redux/actions/ProcedureActions";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../components/loader/Loader";
import countries from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";
import { updateOdooAccount } from "../../../redux/actions/CompanyActions";
import { StatusBadge } from "../../../components/dashboard/DashboardUI";
import { ACTIVITY_SECTORS } from "../../../utils/requestDetails";

const SUBSCRIPTION_TYPES = {
  ManagementRequest: "Gestion d'entreprise",
  SalesRequest: "Gestion de la relation client",
};

countries.registerLocale(fr);

function ServiceRequestDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { serviceRequest } = location.state || {};
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const { token } = useSelector((state) => state.authUser.user);
  const [formData, setFormData] = useState({
    url: "",
    email: "",
    password: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState("");
  const handleModalClose = () => setShowModal(false);
  const handleModalShow = (action) => {
    setAction(action);
    setShowModal(true);
  };

  const handleAction = (action) => {
    Swal.fire({
      title: `Voulez-vous vraiment ${
        action === "in_progress"
          ? "traiter"
          : action === "fulfilled"
          ? "valider"
          : action === "failed" && "rejeter"
      } cette demarche ?`,
      text: "Cette action est irréversible.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#ccc",
      confirmButtonText: "Confirmer",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setSubmitting(true);

        if (action === "in_progress") {
          dispatch(
            assignProcedure(
              token,
              { request: serviceRequest?.reference },
              handleCallback
            )
          );
        } else if (action === "fulfilled") {
          await dispatch(
            updateOdooAccount(
              token,
              serviceRequest?.company?._id,
              { ...formData },
              handleOdooCallback
            )
          );
        } else if (action === "failed") {
          dispatch(
            rejectProcedure(
              token,
              { request: serviceRequest?.reference, status: action },
              handleCallback
            )
          );
        }
      }
    });
  };

  const handleOdooCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      dispatch(
        closeProcedure(
          token,
          {
            request: serviceRequest?.reference,
            status: action,
          },
          handleCallback
        )
      );
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
      setSubmitting(false);
    }
  };

  const handleCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      Toast.fire({
        icon: "success",
        title: "Succès",
        text: message,
      });
      navigate(-1);
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }
    setSubmitting(false);
  };

  const company = serviceRequest?.company || {};
  const owner = serviceRequest?.user || company.user || {};
  const countryName = (code) => countries.getName(code, "fr") || code || "—";

  return (
    <>
      <Loader isLoading={submitting} />
      <div className="dash-page-header">
        <div>
          <button
            type="button"
            className="dash-back"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft aria-hidden="true" /> Retour
          </button>
          <h1 className="dash-page-title">
            {SUBSCRIPTION_TYPES[serviceRequest?.type] ||
              "Demande de souscription"}
          </h1>
          <p className="dash-page-lead">
            #{serviceRequest?.reference} ·{" "}
            {moment(serviceRequest?.createdAt).format("DD/MM/YYYY")}
          </p>
        </div>
        <div className="dash-header-actions">
          <StatusBadge status={serviceRequest?.status} />
          {serviceRequest?.status === "paid" && !serviceRequest?.operator && (
            <button
              type="button"
              className="dash-button"
              onClick={() => handleAction("in_progress")}
            >
              Prendre en charge
            </button>
          )}
          {serviceRequest?.status === "in_progress" &&
            serviceRequest?.operator && (
              <button
                type="button"
                className="dash-button"
                onClick={() => handleModalShow("fulfilled")}
              >
                Valider
              </button>
            )}
        </div>
      </div>

      <div className="dash-detail-grid">
        <section className="dash-card">
          <h2 className="dash-card-title">Entreprise</h2>
          <dl className="dash-details">
            <dt>Nom</dt>
            <dd>{company.businessName || "—"}</dd>
            <dt>Forme juridique</dt>
            <dd>{company.businessType || "—"}</dd>
            <dt>Secteur d'activité</dt>
            <dd>
              {ACTIVITY_SECTORS[company.activitySector] ||
                company.activitySector ||
                "—"}
            </dd>
            {company.managerName && (
              <>
                <dt>Gérant</dt>
                <dd>{company.managerName}</dd>
              </>
            )}
            <dt>Pays</dt>
            <dd>{countryName(serviceRequest?.country || company.country)}</dd>
            {company.address && (
              <>
                <dt>Siège social</dt>
                <dd>{company.address}</dd>
              </>
            )}
            {company.bank && (
              <>
                <dt>Banque</dt>
                <dd>{company.bank}</dd>
              </>
            )}
          </dl>
        </section>

        <aside className="dash-card">
          <h2 className="dash-card-title">Propriétaire</h2>
          <dl className="dash-details is-stacked">
            <dt>Nom</dt>
            <dd>
              {[owner.firstname, owner.lastname].filter(Boolean).join(" ") ||
                "—"}
            </dd>
            <dt>E-mail</dt>
            <dd>{owner.email || "—"}</dd>
            <dt>Téléphone</dt>
            <dd>{owner.mobile || "—"}</dd>
            {owner.whatsApp && (
              <>
                <dt>WhatsApp</dt>
                <dd>{owner.whatsApp}</dd>
              </>
            )}
            {owner.country && (
              <>
                <dt>Pays de résidence</dt>
                <dd>{countryName(owner.country)}</dd>
              </>
            )}
          </dl>
        </aside>
      </div>

      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Paramètres de connexion Odoo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleAction(action);
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label>Adresse de l'espace Odoo du client</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://entreprise.odoo.com"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Entrez l'email de l'entreprise"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Mot de passe</Form.Label>
              <Form.Control
                type="password"
                placeholder="Entrez le mot de passe Odoo"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
              />
            </Form.Group>
            <div className="text-end">
              <Button
                variant="secondary"
                onClick={handleModalClose}
                className="me-2"
              >
                Annuler
              </Button>
              <Button variant="primary" type="submit">
                Soumettre
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ServiceRequestDetails;
